import { Pencil, Trash2, Copy } from "lucide-react";
import { Link } from "react-router-dom";

const STATUS_BADGE = {
  Draft: "bg-gray-100 text-gray-700",
  "Offered to Vendor": "bg-purple-100 text-purple-700",
  "Offer Accepted": "bg-green-100 text-green-700",
  "Offer Rejected": "bg-red-100 text-red-700",
  Started: "bg-blue-100 text-blue-700",
  Completed: "bg-teal-100 text-teal-700",
  Hold: "bg-yellow-100 text-yellow-700",
  "Completion Accepted": "bg-green-100 text-green-700",
  "Completion Rejected": "bg-red-100 text-red-700",
  Cancelled: "bg-gray-100 text-gray-700",
};

const JobsTable = ({
  data = [],
  selectedJobs = [],
  setSelectedJobs,
  onDelete,
  onClone,
  onEdit,
}) => {
  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedJobs(data.map((job) => job.id));
    } else {
      setSelectedJobs([]);
    }
  };

  const handleSelectJob = (jobId) => {
    if (selectedJobs.includes(jobId)) {
      setSelectedJobs(selectedJobs.filter((id) => id !== jobId));
    } else {
      setSelectedJobs([...selectedJobs, jobId]);
    }
  };

  const HEADERS = [
    "Code",
    "Name",
    "Service",
    "Language Pair",
    "Deadline At",
    "Vendor",
    "Status",
    "Start At",
    "Completed At",
    "Work Amount",
    "Payables, INR",
    "Project",
    "Primary Project Manager",
    "Actions",
  ];

  const COL_WIDTHS = ["3%","2%","20%","5%","10%","6%","8%","5%","4%","5%","5%","5%","5%","5%","5%"];

  return (
    <div className="w-full bg-white shadow rounded-lg overflow-hidden">
      <div className="max-h-[600px] overflow-y-auto overflow-x-auto">
        <table className="w-full table-auto border-collapse min-w-[1500px]">
          <colgroup>
            {COL_WIDTHS.map((w, i) => <col key={i} style={{ width: w }} />)}
          </colgroup>

          <thead className="sticky top-0 z-10">
            <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
              <th className="px-3 py-2 text-center font-semibold whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={data.length > 0 && selectedJobs.length === data.length}
                  onChange={handleSelectAll}
                  className="w-3.5 h-3.5 accent-blue-600 cursor-pointer"
                />
              </th>
              {HEADERS.map((h) => (
                <th key={h} className="px-3 py-2 text-center font-semibold whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={HEADERS.length + 1} className="py-10 text-center text-gray-400 text-xs">
                  No jobs found.
                </td>
              </tr>
            ) : (
              data.map((row, index) => {
                const rowBg = index % 2 === 0 ? "bg-gray-50" : "bg-white";
                const statusCls = STATUS_BADGE[row.status] || "bg-gray-100 text-gray-700";

                return (
                  <tr key={row.id} className={`${rowBg} hover:bg-blue-50 transition-colors`}>
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={selectedJobs.includes(row.id)}
                        onChange={() => handleSelectJob(row.id)}
                        className="w-3.5 h-3.5 accent-blue-600 cursor-pointer"
                      />
                    </td>

                    <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                      <Link
                        to={`/project/${row.project_id}/job/${row.id}`}
                        className="text-blue-600 hover:text-blue-800 font-medium underline"
                      >
                        J{row.id}
                      </Link>
                    </td>

                    <td className="px-3 py-2 text-xs break-words text-gray-900">
                      {row.name || "—"}
                    </td>

                    <td className="px-3 py-2 text-xs break-words text-gray-700">
                      {row.service?.name || "—"}
                    </td>

                    <td className="px-3 py-2 text-xs break-words text-gray-700">
                      {row.languagePair
                        ? `${row.languagePair.sourceLanguage?.name || ""} → ${row.languagePair.targetLanguage?.name || ""}`
                        : "—"}
                    </td>

                    <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-500">
                      {formatDateTime(row.deadline_at)}
                    </td>

                    <td className="px-3 py-2 text-xs break-words">
                      {row.vendor?.id ? (
                        <Link
                          to={`/vendors/${row.vendor.id}`}
                          className="text-blue-600 hover:text-blue-800 underline font-medium"
                        >
                          {row.vendor.company_name ||
                            `${row.vendor.primary_users?.first_name || ""} ${
                              row.vendor.primary_users?.last_name || ""
                            }`.trim() ||
                            "—"}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </td>

                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${statusCls}`}>
                        {row.status}
                      </span>
                    </td>

                    <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-500">
                      {formatDateTime(row.started_at)}
                    </td>

                    <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-500">
                      {formatDateTime(row.completed_at)}
                    </td>

                    <td className="px-3 py-2 text-xs text-right whitespace-nowrap text-gray-700">
                      {row.work_amount || "—"}
                    </td>

                    <td className="px-3 py-2 text-xs font-semibold text-right whitespace-nowrap text-gray-900">
                      {row.payables_amount
                        ? `₹${parseFloat(row.payables_amount).toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`
                        : "—"}
                    </td>

                    <td className="px-3 py-2 text-xs break-words">
                      <Link
                        to={`/project/${row.project_id}`}
                        className="text-blue-600 hover:text-blue-800 underline font-medium"
                      >
                        {row.project?.project_name || "—"}
                      </Link>
                    </td>

                    <td className="px-3 py-2 text-xs break-words text-gray-700">
                      {row.project?.primaryManager
                        ? `${row.project.primaryManager.first_name} ${row.project.primaryManager.last_name}`
                        : "—"}
                    </td>

                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      <div className="flex justify-center gap-3">
                        <Copy
                          className="w-4 h-4 text-gray-400 hover:text-gray-700 cursor-pointer transition-colors"
                          title="Clone Job"
                          onClick={() => onClone(row.id)}
                        />
                        <Pencil
                          className="w-4 h-4 text-blue-400 hover:text-blue-600 cursor-pointer transition-colors"
                          title="Edit Job"
                          onClick={() => onEdit(row.id)}
                        />
                        <Trash2
                          className="w-4 h-4 text-red-400 hover:text-red-600 cursor-pointer transition-colors"
                          title="Delete Job"
                          onClick={() => onDelete(row.id)}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default JobsTable;
